import React, { useState } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { FuelGauge } from '../vehicle/FuelGauge';
import { Vehicle, Driver } from '../../types';
import theme from '../../theme';

interface CarDeliveryFormData {
  vehicleId: string;
  driverId: string;
  secondaryDriverId?: string;
  deliveredBy: string;
  deliveredAt: string;
  mileageOut: number;
  fuelLevel: number;
  workCompleted: string[];
  repairsCompleted: string[];
  remainingIssues: string[];
  deliveryNotes: string;
  keyStatus: 'delivered' | 'missing' | 'damaged';
  documentsReturned: string[];
  qualityCheckPassed: boolean;
  followUpRequired: boolean;
  followUpDate?: string;
  followUpNotes?: string;
}

interface CarDeliveryFormProps {
  vehicle?: Vehicle;
  driver?: Driver;
  secondaryDriver?: Driver;
  prefilledData?: Partial<CarDeliveryFormData>;
  onSubmit: (data: CarDeliveryFormData) => void;
  onCancel: () => void;
}

export const CarDeliveryForm: React.FC<CarDeliveryFormProps> = ({
  vehicle,
  driver,
  secondaryDriver,
  prefilledData,
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState<CarDeliveryFormData>({
    vehicleId: prefilledData?.vehicleId || vehicle?.id || '',
    driverId: prefilledData?.driverId || driver?.id || '',
    secondaryDriverId: prefilledData?.secondaryDriverId || secondaryDriver?.id,
    deliveredBy: 'Fleet Manager', // Default value
    deliveredAt: new Date().toISOString().slice(0, 16),
    mileageOut: prefilledData?.mileageOut || 0,
    fuelLevel: 100,
    workCompleted: [],
    repairsCompleted: [],
    remainingIssues: [],
    deliveryNotes: '',
    keyStatus: 'delivered',
    documentsReturned: [],
    qualityCheckPassed: true,
    followUpRequired: false,
  });

  const updateFormData = (field: keyof CarDeliveryFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!formData.vehicleId || !formData.driverId || !formData.deliveredBy) {
      Alert.alert('Error', 'Please fill in all required fields');
      return;
    }
    onSubmit(formData);
  };

  const toggleArrayItem = (array: string[], item: string, field: keyof CarDeliveryFormData) => {
    const currentArray = formData[field] as string[];
    const newArray = currentArray.includes(item)
      ? currentArray.filter(i => i !== item)
      : [...currentArray, item];
    updateFormData(field, newArray);
  };

  return (
    <View style={styles.container}>
      {/* A4 Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <Ionicons name="car" size={32} color={theme.colors.primary} />
          <View style={styles.headerText}>
            <Text style={styles.headerTitle}>VEHICLE DELIVERY FORM</Text>
            <Text style={styles.headerSubtitle}>STSC Fleet Management</Text>
          </View>
        </View>
        <Text style={styles.formNumber}>Form #: VDF-{new Date().getTime()}</Text>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Delivery Information */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Delivery Information</Text>
          
          {vehicle && (
            <View style={styles.vehicleInfo}>
              <Text style={styles.infoTitle}>Vehicle Details</Text>
              <Text style={styles.infoText}>Plate: {vehicle.licensePlate}</Text>
              <Text style={styles.infoText}>Make/Model: {vehicle.makeModel}</Text>
              <Text style={styles.infoText}>Year: {vehicle.year}</Text>
              <Text style={styles.infoText}>Fleet Type: {vehicle.fleetType}</Text>
            </View>
          )}

          {driver && (
            <View style={styles.driverInfo}>
              <Text style={styles.infoTitle}>Primary Driver Details</Text>
              <Text style={styles.infoText}>Name: {driver.name}</Text>
              <Text style={styles.infoText}>ID: {driver.licenseNumber}</Text>
              <Text style={styles.infoText}>Phone: {driver.phoneNumber}</Text>
            </View>
          )}

          {secondaryDriver && (
            <View style={[styles.driverInfo, styles.secondaryDriverInfo]}>
              <Text style={[styles.infoTitle, styles.secondaryDriverTitle]}>Secondary Driver Details</Text>
              <Text style={styles.infoText}>Name: {secondaryDriver.name}</Text>
              <Text style={styles.infoText}>ID: {secondaryDriver.licenseNumber}</Text>
              <Text style={styles.infoText}>Phone: {secondaryDriver.phone}</Text>
            </View>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Delivered By *</Text>
            <TextInput
              style={styles.textInput}
              value={formData.deliveredBy}
              onChangeText={(value) => updateFormData('deliveredBy', value)}
              placeholder="Enter delivery person name"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Delivery Date & Time *</Text>
            <TextInput
              style={styles.textInput}
              value={formData.deliveredAt}
              onChangeText={(value) => updateFormData('deliveredAt', value)}
              placeholder="YYYY-MM-DD HH:MM"
            />
          </View>
        </Card>

        {/* Vehicle Return Condition */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Vehicle Return Condition</Text>
          
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Final Mileage</Text>
            <TextInput
              style={styles.textInput}
              value={formData.mileageOut.toString()}
              onChangeText={(value) => updateFormData('mileageOut', parseInt(value) || 0)}
              keyboardType="numeric"
              placeholder="Enter final mileage"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Fuel Level</Text>
            <FuelGauge 
              value={formData.fuelLevel} 
              onChange={(value) => updateFormData('fuelLevel', value)}
              editable={true}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Key Status</Text>
            <View style={styles.radioGroup}>
              {[
                { value: 'delivered', label: 'Delivered' },
                { value: 'missing', label: 'Missing' },
                { value: 'damaged', label: 'Damaged' }
              ].map((option) => (
                <TouchableOpacity
                  key={option.value}
                  style={styles.radioOption}
                  onPress={() => updateFormData('keyStatus', option.value)}
                >
                  <Ionicons
                    name={formData.keyStatus === option.value ? 'radio-button-on' : 'radio-button-off'}
                    size={20}
                    color={theme.colors.primary}
                  />
                  <Text style={styles.radioText}>{option.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </Card>

        {/* Work Completed */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Work Completed</Text>
          
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Services Performed</Text>
            {[
              'Oil Change',
              'Tire Rotation',
              'Brake Check',
              'Engine Service',
              'AC Service',
              'General Maintenance',
              'Body Work',
              'Interior Cleaning'
            ].map((service) => (
              <TouchableOpacity
                key={service}
                style={styles.checkboxOption}
                onPress={() => toggleArrayItem(formData.workCompleted, service, 'workCompleted')}
              >
                <Ionicons
                  name={formData.workCompleted.includes(service) ? 'checkbox' : 'checkbox-outline'}
                  size={20}
                  color={theme.colors.primary}
                />
                <Text style={styles.checkboxText}>{service}</Text>
              </TouchableOpacity>
            ))}
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Repairs Completed</Text>
            <TextInput
              style={[styles.textInput, styles.textArea]}
              value={formData.repairsCompleted.join('\n')}
              onChangeText={(value) => updateFormData('repairsCompleted', value.split('\n').filter(Boolean))}
              placeholder="List completed repairs (one per line)"
              multiline
              numberOfLines={3}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Remaining Issues</Text>
            <TextInput
              style={[styles.textInput, styles.textArea]}
              value={formData.remainingIssues.join('\n')}
              onChangeText={(value) => updateFormData('remainingIssues', value.split('\n').filter(Boolean))}
              placeholder="List any remaining issues (one per line)"
              multiline
              numberOfLines={3}
            />
          </View>
        </Card>

        {/* Quality Check */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Quality Check</Text>
          
          <TouchableOpacity
            style={styles.checkboxOption}
            onPress={() => updateFormData('qualityCheckPassed', !formData.qualityCheckPassed)}
          >
            <Ionicons
              name={formData.qualityCheckPassed ? 'checkbox' : 'checkbox-outline'}
              size={20}
              color={theme.colors.primary}
            />
            <Text style={styles.checkboxText}>Quality check passed</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.checkboxOption}
            onPress={() => updateFormData('followUpRequired', !formData.followUpRequired)}
          >
            <Ionicons
              name={formData.followUpRequired ? 'checkbox' : 'checkbox-outline'}
              size={20}
              color={theme.colors.primary}
            />
            <Text style={styles.checkboxText}>Follow-up required</Text>
          </TouchableOpacity>

          {formData.followUpRequired && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Follow-up Date</Text>
                <TextInput
                  style={styles.textInput}
                  value={formData.followUpDate || ''}
                  onChangeText={(value) => updateFormData('followUpDate', value)}
                  placeholder="YYYY-MM-DD"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Follow-up Notes</Text>
                <TextInput
                  style={[styles.textInput, styles.textArea]}
                  value={formData.followUpNotes || ''}
                  onChangeText={(value) => updateFormData('followUpNotes', value)}
                  placeholder="Enter follow-up requirements"
                  multiline
                  numberOfLines={3}
                />
              </View>
            </>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Delivery Notes</Text>
            <TextInput
              style={[styles.textInput, styles.textArea]}
              value={formData.deliveryNotes}
              onChangeText={(value) => updateFormData('deliveryNotes', value)}
              placeholder="Enter any additional delivery notes"
              multiline
              numberOfLines={4}
            />
          </View>
        </Card>

        {/* Signatures */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Signatures</Text>
          
          <View style={styles.signatureRow}>
            <View style={styles.signatureBox}>
              <Text style={styles.signatureLabel}>Driver Signature</Text>
              <View style={styles.signatureLine} />
              <Text style={styles.signatureDate}>Date: ________________</Text>
            </View>
            
            <View style={styles.signatureBox}>
              <Text style={styles.signatureLabel}>Delivery Person Signature</Text>
              <View style={styles.signatureLine} />
              <Text style={styles.signatureDate}>Date: ________________</Text>
            </View>
          </View>
        </Card>
      </ScrollView>

      {/* Action Buttons */}
      <View style={styles.footer}>
        <Button
          title="Cancel"
          variant="outline"
          onPress={onCancel}
          style={styles.footerButton}
        />
        <Button
          title="Complete Delivery"
          onPress={handleSubmit}
          style={styles.footerButton}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.white,
  },
  header: {
    backgroundColor: theme.colors.white,
    padding: theme.spacing.lg,
    borderBottomWidth: 2,
    borderBottomColor: theme.colors.primary,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerText: {
    marginLeft: theme.spacing.md,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
  },
  headerSubtitle: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  formNumber: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    fontFamily: 'monospace',
  },
  content: {
    flex: 1,
    padding: theme.spacing.lg,
  },
  section: {
    marginBottom: theme.spacing.lg,
    padding: theme.spacing.lg,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
    paddingBottom: theme.spacing.sm,
  },
  vehicleInfo: {
    marginBottom: theme.spacing.md,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.md,
  },
  driverInfo: {
    marginBottom: theme.spacing.md,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  secondaryDriverInfo: {
    backgroundColor: '#f0f9ff',
    borderLeftWidth: 3,
    borderLeftColor: theme.colors.success,
  },
  secondaryDriverTitle: {
    color: theme.colors.success,
  },
  infoTitle: {
    fontSize: 14,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
  },
  infoText: {
    fontSize: 13,
    color: theme.colors.textSecondary,
    marginBottom: 2,
  },
  inputGroup: {
    marginBottom: theme.spacing.md,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
  },
  textInput: {
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.sm,
    fontSize: 14,
    color: theme.colors.textPrimary,
    backgroundColor: theme.colors.white,
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  radioGroup: {
    flexDirection: 'column',
  },
  radioOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.xs,
  },
  radioText: {
    marginLeft: theme.spacing.xs,
    fontSize: 13,
    color: theme.colors.textPrimary,
  },
  checkboxOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.xs,
  },
  checkboxText: {
    marginLeft: theme.spacing.xs,
    fontSize: 13,
    color: theme.colors.textPrimary,
  },
  signatureRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  signatureBox: {
    flex: 0.48,
    alignItems: 'center',
  },
  signatureLabel: {
    fontSize: 14,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  signatureLine: {
    width: '100%',
    height: 60,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray400,
    marginBottom: theme.spacing.sm,
  },
  signatureDate: {
    fontSize: 12,
    color: theme.colors.textSecondary,
  },
  footer: {
    flexDirection: 'row',
    padding: theme.spacing.lg,
    borderTopWidth: 1,
    borderTopColor: theme.colors.gray200,
    backgroundColor: theme.colors.white,
  },
  footerButton: {
    flex: 1,
    marginHorizontal: theme.spacing.xs,
  },
});

export default CarDeliveryForm;
